import { useState } from "react";
import confetti from "canvas-confetti";
import { Crown, Sword, CheckCircle2, ShieldCheck } from "lucide-react";
import { Tv, Ghost, Pizza, ShoppingBag, CreditCard, Music, Cloud, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";

const ICONS = { Tv, Ghost, Pizza, ShoppingBag, CreditCard, Music, Cloud, Dumbbell };

function fireBossConfetti() {
  const base = { ticks: 140, gravity: 0.85, decay: 0.93, startVelocity: 50, scalar: 1.05 };
  // bigger, gold-heavy burst with two follow-ups
  confetti({ ...base, particleCount: 130, spread: 90, origin: { y: 0.55 }, colors: ["#F59E0B", "#FBBF24", "#FDE68A", "#0F172A"] });
  confetti({ ...base, particleCount: 60, angle: 60,  spread: 60, origin: { x: 0, y: 0.7 }, colors: ["#F59E0B", "#FBBF24"] });
  confetti({ ...base, particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors: ["#F59E0B", "#FBBF24"] });
  window.setTimeout(() => {
    confetti({ ...base, particleCount: 80, spread: 110, origin: { y: 0.5 }, colors: ["#F59E0B", "#FBBF24", "#FFFFFF"] });
  }, 280);
}

/**
 * BossBattleCard — pinned weekly highlight at the top of the dashboard.
 * If `defeatedThisWeek`, shows a "champion" state instead of the defeat CTA.
 */
export default function BossBattleCard({ sub, defeatedThisWeek, onBossDefeat }) {
  const [defeating, setDefeating] = useState(false);
  const Icon = ICONS[sub?.icon] || CreditCard;

  const handleDefeat = () => {
    if (defeating || !sub) return;
    setDefeating(true);
    fireBossConfetti();
    window.setTimeout(() => onBossDefeat?.(sub.id, sub), 600);
  };

  if (!sub) return null;

  return (
    <div
      className="relative overflow-hidden rounded-3xl border-2 p-5 mmh-fade-up"
      style={{
        background: defeatedThisWeek
          ? "linear-gradient(135deg, #064e3b 0%, #0f172a 100%)"
          : "linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #312E81 100%)",
        borderColor: "#FBBF24",
        boxShadow: "0 16px 36px -12px rgba(245,158,11,0.45), 0 0 0 4px rgba(251,191,36,0.18)",
      }}
      data-testid="boss-battle-card"
    >
      {/* gold sparkles */}
      <div aria-hidden="true" className="absolute -top-12 -right-12 w-44 h-44 rounded-full" style={{ background: "radial-gradient(circle, rgba(251,191,36,0.35), rgba(251,191,36,0) 60%)" }} />
      <div aria-hidden="true" className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full" style={{ background: "radial-gradient(circle, rgba(99,102,241,0.35), rgba(99,102,241,0) 60%)" }} />

      <div className="relative flex items-center gap-3 mb-3">
        <span
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.18em] text-slate-900"
          style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B)" }}
        >
          <Crown className="w-3 h-3" strokeWidth={3} /> Weekly Boss
        </span>
        <span className="text-[10px] font-bold tracking-widest text-amber-300/80 uppercase">2× streak credit</span>
      </div>

      <div className="relative flex items-start gap-4">
        <div
          className="flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-slate-900 ring-4 ring-amber-400/40 shadow-lg"
          style={{ background: "linear-gradient(135deg,#FBBF24,#F59E0B)" }}
        >
          <Icon className="w-8 h-8" strokeWidth={2.6} />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="text-xl font-black text-white leading-tight truncate"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="boss-battle-name"
          >
            {sub.name}
          </h3>
          <p className="text-xs font-semibold text-amber-300/80 mt-0.5 uppercase tracking-wider">
            Biggest drain this week
          </p>
          <div
            className="mt-2 text-3xl font-black text-amber-300 leading-none"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="boss-battle-cost"
          >
            ${sub.monthly_cost.toFixed(2)}
            <span className="text-sm font-bold text-amber-300/70 ml-1">/month</span>
          </div>
        </div>
      </div>

      {defeatedThisWeek ? (
        <div
          className="relative mt-4 flex items-center justify-center gap-2 h-12 rounded-2xl bg-emerald-500/15 border-2 border-emerald-400/40 text-emerald-200 font-black tracking-wide"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="boss-battle-defeated-state"
        >
          <ShieldCheck className="w-5 h-5" /> Boss defeated for this week
        </div>
      ) : (
        <Button
          onClick={handleDefeat}
          disabled={defeating}
          className="relative mt-4 w-full h-12 rounded-2xl bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white font-black tracking-wide shadow-lg hover:shadow-xl focus:ring-4 focus:ring-amber-400/40"
          style={{ fontFamily: "Outfit, sans-serif" }}
          data-testid="boss-defeat-button"
        >
          {defeating ? (<><CheckCircle2 className="w-5 h-5 mr-2" /> Slaying boss…</>) : (<><Sword className="w-5 h-5 mr-2" strokeWidth={2.8} /> Slay the Boss</>)}
        </Button>
      )}
    </div>
  );
}
